import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Building2, GraduationCap, Mail, Users, BookOpen, Award, X, ExternalLink } from "lucide-react";

const ROLE_BADGE = {
  client: "border-sky-200 bg-sky-50 text-sky-700",
  student: "border-violet-200 bg-violet-50 text-violet-700",
  mentor: "border-emerald-200 bg-emerald-50 text-emerald-700",
  tutor: "border-amber-200 bg-amber-50 text-amber-700",
  partner_institution: "border-indigo-200 bg-indigo-50 text-indigo-700",
  admin: "border-rose-200 bg-rose-50 text-rose-700",
};

/**
 * Inline clickable user name. Click to open a quick-look profile drawer.
 */
export default function UserProfileLink({ userId, name, className = "" }) {
  const [open, setOpen] = useState(false);
  if (!userId) return <span className={className}>{name || "—"}</span>;
  return (
    <>
      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={`text-kb-primary hover:underline inline-flex items-center gap-1 ${className}`}
        data-testid={`profile-link-${userId}`}>
        {name || userId}
      </button>
      {open && <ProfileDrawer userId={userId} onClose={() => setOpen(false)} />}
    </>
  );
}

function ProfileDrawer({ userId, onClose }) {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get(`/users/${userId}/profile`).then((r) => setProfile(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load profile"));
  }, [userId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="profile-drawer">
      <div className="bg-white rounded-sm max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">USER PROFILE</div>
          <button onClick={onClose} className="p-1 text-kb-ink-muted hover:text-kb-primary" data-testid="profile-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6">
          {!profile && !error && <div className="kb-mono text-xs text-kb-ink-muted">Loading…</div>}
          {error && <div className="text-sm text-rose-600">{error}</div>}
          {profile && (
            <>
              <div className="flex items-start gap-4">
                <img src={profile.picture || profile.mentor?.photo_url || "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200"}
                  alt={profile.name} className="w-16 h-16 rounded-sm border border-slate-200 object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-xl text-kb-ink">{profile.name}</div>
                  <span className={`kb-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border inline-block mt-1 ${ROLE_BADGE[profile.role] || ""}`}>
                    {profile.role?.replace("_", " ")}
                  </span>
                </div>
              </div>

              <div className="mt-5 space-y-2 text-sm">
                {profile.affiliation && (
                  <div className="flex items-center gap-2 text-kb-ink-muted">
                    <Building2 className="w-3.5 h-3.5" /> {profile.affiliation}
                  </div>
                )}
                {profile.academic_level && (
                  <div className="flex items-center gap-2 text-kb-ink-muted">
                    <GraduationCap className="w-3.5 h-3.5" /> {profile.academic_level}{profile.student_type ? ` · ${profile.student_type}` : ""}
                  </div>
                )}
                <div className="kb-mono text-[10px] text-kb-ink-light">
                  Joined {new Date(profile.joined_at).toLocaleDateString()} · {profile.verified ? <span className="text-emerald-600">VERIFIED</span> : <span className="text-amber-600">PENDING</span>}
                </div>
              </div>

              {profile.domains?.length > 0 && (
                <div className="mt-4">
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2">RESEARCH DOMAINS</div>
                  <div className="flex flex-wrap gap-1.5">
                    {profile.domains.map((d) => (
                      <span key={d} className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink bg-slate-100 px-2 py-1 rounded-sm">{d}</span>
                    ))}
                  </div>
                </div>
              )}

              {profile.mentor && (
                <div className="mt-4 bg-slate-50 border border-slate-200 rounded-sm p-3 text-sm">
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary">MENTOR PROFILE</div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div><div className="kb-mono text-[10px] text-kb-ink-light">H-INDEX</div><div className="font-mono text-kb-primary">{profile.mentor.h_index}</div></div>
                    <div><div className="kb-mono text-[10px] text-kb-ink-light">EXPERTISE</div><div className="text-xs">{(profile.mentor.expertise || []).join(", ") || "—"}</div></div>
                  </div>
                  {profile.mentor.bio && <div className="text-xs text-kb-ink-muted mt-2">{profile.mentor.bio}</div>}
                </div>
              )}

              <div className="mt-5 grid grid-cols-2 gap-2 pt-4 border-t border-slate-200">
                <div className="flex items-center gap-2 text-sm"><Users className="w-3.5 h-3.5 text-kb-ink-light" /> <span className="kb-mono text-[10px] text-kb-ink-light">IDEAS</span> <span className="font-mono text-kb-ink">{profile.counts?.ideas || 0}</span></div>
                <div className="flex items-center gap-2 text-sm"><BookOpen className="w-3.5 h-3.5 text-kb-ink-light" /> <span className="kb-mono text-[10px] text-kb-ink-light">COURSES</span> <span className="font-mono text-kb-ink">{profile.counts?.courses_authored || 0}</span></div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
